// The standalone driver profile. Standing is the frame, and every invalid requirement
// under it carries the action that resolves it.
//
// The state, the loads and the four writes now live in `useDriverRecord`, and the body
// in `RecordPanel`, so the merged user page (`pages/UserProfile`) hosts the same record
// inside its Driver tab without a second copy of any of it. What is left here is this
// page's own chrome: header, standing pill, layout.
//
// This route is kept rather than redirected: the drivers list and the review queue
// still land on it, and a reviewer working a queue does not want four tabs of a person
// they are not looking at. The user page is the surface for the whole person; this one
// is the surface for their documents.
import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Box, Paper, Stack, Typography, CircularProgress, Button, IconButton, ProfileAvatar, Link } from '../../ui/core';
import { flushPageSx, hairline, scrollY, surfaceCardSx } from '../../ui/styles';
import DashboardLayout from '../../layouts/DashoardLayout';
import { ChevronLeftIcon } from '../../ui/icons';
import { StandingPill } from './components';
import RecordPanel from './RecordPanel';
import Inspector from './Inspector';
import useDriverRecord from './useDriverRecord';

/** Everything the header needs, from the funnel record. */
const identity = (driver) => ({
  name: [driver?.firstName, driver?.lastName].filter(Boolean).join(' ') || 'Unknown driver',
  city: driver?.homeCity || null,
  phone: driver?.phoneNumber || null,
  type: driver?.driverType || null,
});

/**
 * Back to wherever they came from, falling back to the driver list.
 *
 * `goBack` rather than a link to /drivers, because the list's filter and page live in
 * its URL — a link would drop a reviewer working through page 4 of the suspended
 * drivers back onto page 1 of everyone. But `location.key` is undefined for the first
 * entry in a session, so on a pasted or bookmarked link there is nothing to go back
 * TO, and goBack would leave the app entirely.
 */
const BackButton = () => {
  const history = useHistory();
  const { key } = useLocation();

  return (
    <IconButton
      size="small"
      aria-label="Back to drivers"
      onClick={() => (key ? history.goBack() : history.push('/drivers'))}
      sx={{ ml: -0.75, color: 'text.secondary' }}
    >
      <ChevronLeftIcon sx={{ fontSize: 22 }} />
    </IconButton>
  );
};

const DriverProfileShell = () => {
  // Whatever the link carried. Not necessarily the id the endpoints want — see
  // resolveDriverId — so nothing below uses this except to resolve.
  const { driverId: routeId } = useParams();
  const history = useHistory();
  const r = useDriverRecord({ routeId });

  if (r.loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', py: 8 }}>
        <CircularProgress size={22} />
      </Stack>
    );
  }

  if (r.error) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <Typography sx={{ fontSize: '14px' }}>{r.error}</Typography>
        <Button variant="outlined" onClick={r.reload} sx={{ textTransform: 'none' }}>
          Try again
        </Button>
      </Stack>
    );
  }

  const person = identity(r.driver);

  return (
    // flex + minHeight:0, never height:100% — the height ceiling comes from
    // flushPageSx below, and this has to be a shrinkable flex child of it so the
    // inspector's document pane can be fixed while only its sidebar scrolls.
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <input
        ref={r.fileInput}
        type="file"
        accept="image/jpeg"
        hidden
        onChange={(e) => r.upload(e.target.files?.[0])}
      />

      {r.selected ? (
        <Inspector
          profile={r.profile}
          requirement={r.selected}
          driverName={person.name}
          photoUrl={r.photoUrl}
          onBack={r.closeItem}
          viewingId={r.viewingId}
          onViewVersion={(rowId) => r.setSearch({ v: rowId }, { replace: true })}
          onStep={r.step}
          stepIndex={r.stepIndex}
          stepCount={r.stepCount}
          onDecide={r.decide}
          onUpload={r.pickFile}
          extraction={r.extraction}
          extractionLoading={r.extractionLoading}
          extractionError={r.extractionError}
          fieldRows={r.fieldRows}
          onSaveFields={r.saveFields}
          onReplay={r.replay}
          busy={r.busy}
        />
      ) : (
        <>
          <Box sx={{ px: 3, pt: 2.5, pb: 1.75, borderBottom: (t) => `1px solid ${hairline(t)}`, flexShrink: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <BackButton />
              {/* Avatar and name link to the user record — this page is about their
                  documents and standing, and everything else about the person lives
                  there. Falls back to plain text without a resolved user id rather
                  than linking to /users/undefined. */}
              <Stack
                component={r.userId ? Link : 'div'}
                {...(r.userId ? { to: `/users/${r.userId}` } : {})}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  textDecoration: 'none',
                  color: 'inherit',
                  minWidth: 0,
                  ...(r.userId ? { cursor: 'pointer', '&:hover': { opacity: 0.75 } } : {}),
                }}
              >
                <ProfileAvatar name={person.name} photoUrl={r.photoUrl} colorKey={r.driverId} size={34} />
                <Typography sx={{ fontSize: '20px', fontWeight: 600, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
                  {person.name}
                </Typography>
              </Stack>
              {/* The pill IS the standing control — no separate standing section */}
              <StandingPill
                standing={r.profile.currentStanding}
                applicationStatus={r.profile.applicationStatus}
                hasBeenApproved={r.profile.hasBeenApproved}
                unresolved={r.profile.blockingCount}
                onAction={r.actOnStanding}
                onReevaluate={r.reevaluateStanding}
                busy={r.busy}
              />
              <Box sx={{ flex: 1 }} />
              <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                {[person.city, person.type].filter(Boolean).join(' · ')}
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={() => history.push(`/drivers-approval/funnel/${r.driverId}`)}
                sx={{ textTransform: 'none', fontSize: '12.5px', whiteSpace: 'nowrap' }}
              >
                Open in approvals
              </Button>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, ...scrollY }}>
            <Box sx={{ maxWidth: 880, mx: 'auto', px: 3, py: 2.5 }}>
              <RecordPanel
                profile={r.profile}
                driver={r.driver}
                mode={r.mode}
                onModeChange={(next) => r.setSearch({ mode: next === 'all' ? null : next }, { replace: true })}
                onSelect={r.select}
                screening={r.screening}
                screeningError={r.screeningError}
                events={r.events}
                busy={r.busy}
                onAddVehicle={r.addVehicle}
                onRemoveVehicle={r.removeVehicle}
                onGrandfatherVehicle={r.setVehicleGrandfathered}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

/**
 * `flush` layout, because this page manages its own scrolling.
 *
 * flushPageSx is what caps the height: the shell's own `height: 100%` capped
 * nothing, since the layout's content box sets `minHeight: 100vh` — a floor, not
 * a ceiling — so the inspector grew past the viewport and pushed the document
 * viewer's zoom and rotate controls below the fold. With a real ceiling plus
 * `overflow: hidden`, the page itself never scrolls and only the inspector's
 * sidebar does.
 */
const DriverProfile = () => (
  <DashboardLayout flush title="Driver profile">
    <Box sx={flushPageSx}>
      <Paper elevation={0} sx={{ ...surfaceCardSx, flex: 1, minWidth: 0 }}>
        <DriverProfileShell />
      </Paper>
    </Box>
  </DashboardLayout>
);

export default DriverProfile;
